import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { updateBookOrder, deleteBookOrder } from '../graphql/mutations';
import { getBook } from '../graphql/queries'; 
import { getUrl } from 'aws-amplify/storage';
import { FiTrash2, FiTrendingUp, FiCheck, FiX } from 'react-icons/fi';

export const OrdersManager = ({ orders, onUpdate }) => {
    const client = generateClient();
    const [filterDate, setFilterDate] = useState('');

    const filteredOrders = orders.filter(o => {
        if (!filterDate) return true;
        return o.orderDate.startsWith(filterDate);
    });

    const stats = filteredOrders.reduce((acc, o) => {
        if (o.status === 'APROBADO' || o.status === 'COMPLETED') {
            const amount = parseFloat(o.amountUSD || 0);
            acc.total += amount;
            const cat = (o.category || 'LIBRO').toUpperCase();
            if (cat.includes('CURSO')) acc.cursos += amount;
            else if (cat.includes('REVISION') || cat.includes('EDICION')) acc.servicios += amount;
            else acc.libros += amount;
        }
        return acc;
    }, { total: 0, libros: 0, cursos: 0, servicios: 0 });

    // --- MEJORA: Visualización de comprobante con ruta completa ---
    const handleViewReceipt = async (receiptKey) => {
        try {
            // Cambio clave: Especificamos el path completo 'public/' para Amplify v6
            const urlRes = await getUrl({ 
                path: `public/${receiptKey}` 
            });
            window.open(urlRes.url.toString(), '_blank');
        } catch (error) {
            console.error("Error S3:", error);
            alert("No se pudo cargar la imagen del pago. Verifica la ruta en S3.");
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm("⚠️ ¿Eliminar permanentemente este registro?")) return;
        try {
            await client.graphql({
                query: deleteBookOrder,
                variables: { input: { id: orderId } },
                authMode: 'userPool',
            });
            onUpdate();
        } catch (error) {
            alert("Error al eliminar.");
        }
    };

    // --- MEJORA: Función de aprobación para vincular PDF ---
    const handleApprove = async (order) => {
        try {
            let bookPdfKey = null;

            // Buscamos el pdfKey real en el libro para dárselo a la orden aprobada
            if (order.bookID && (order.category === 'LIBRO' || order.category === 'PDF')) {
                const bookData = await client.graphql({
                    query: getBook,
                    variables: { id: order.bookID },
                    authMode: 'userPool',
                });
                bookPdfKey = bookData.data.getBook?.pdfKey;
            }

            await client.graphql({
                query: updateBookOrder,
                variables: { 
                    input: { 
                        id: order.id, 
                        status: 'APROBADO',
                        pdfKey: bookPdfKey // Se vincula el PDF para que el cliente lo vea en su perfil
                    } 
                },
                authMode: 'userPool',
            });

            alert("✅ Pago aprobado. El cliente ya tiene acceso a su contenido.");
            onUpdate();
        } catch (error) { 
            console.error("Error al aprobar:", error);
            alert("Error al procesar la aprobación.");
        }
    };

    const handleReject = async (orderId) => {
        if (!window.confirm("¿Rechazar este pago?")) return;
        try {
            await client.graphql({
                query: updateBookOrder,
                variables: { input: { id: orderId, status: 'RECHAZADO' } },
                authMode: 'userPool',
            });
            onUpdate();
        } catch (error) { console.error(error); }
    };

    return (
        <div className="orders-manager">
            {/* Cabecera de Estadísticas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                <div className="stat-card" style={{ background: '#001f3f', color: 'white', padding: '20px', borderRadius: '10px' }}>
                    <small>INGRESOS TOTALES</small>
                    <h2 style={{ color: '#D4AF37' }}>${stats.total.toFixed(2)}</h2>
                    <FiTrendingUp />
                </div>
                <div className="stat-card" style={{ background: '#fff', border: '1px solid #ddd', padding: '20px', borderRadius: '10px' }}>
                    <small>LIBROS</small>
                    <h3>${stats.libros.toFixed(2)}</h3>
                </div>
                <div className="stat-card" style={{ background: '#fff', border: '1px solid #ddd', padding: '20px', borderRadius: '10px' }}>
                    <small>CURSOS</small>
                    <h3>${stats.cursos.toFixed(2)}</h3>
                </div>
                <div className="stat-card" style={{ background: '#fff', border: '1px solid #ddd', padding: '20px', borderRadius: '10px' }}>
                    <small>REVISIÓN / EDICIÓN</small>
                    <h3>${stats.servicios.toFixed(2)}</h3>
                </div>
            </div>

            {/* Filtros */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label>Filtrar por Fecha:</label>
                <input type="date" className="pro-input" onChange={(e) => setFilterDate(e.target.value)} />
                <button onClick={() => setFilterDate('')} className="btn-view-small">Limpiar</button>
            </div>

            <h4 style={{ marginBottom: '15px', color: '#D4AF37' }}>📊 Auditoría de Ventas Detallada</h4>
            
            {/* Tabla de Órdenes */}
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                <table className="admin-table-simple">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Cliente / Datos</th>
                            <th>Producto / Formato</th>
                            <th>Monto (USD/Bs)</th>
                            <th>Referencia / Banco</th>
                            <th>Comprobante</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.sort((a,b) => new Date(b.orderDate) - new Date(a.orderDate)).map(o => (
                            <tr key={o.id}>
                                <td>{new Date(o.orderDate).toLocaleDateString()}</td>
                                <td>
                                    <strong>{o.customerName}</strong><br/>
                                    <small>{o.customerEmail}</small><br/>
                                    <small>ID: {o.idNumber}</small>
                                </td>
                                <td>
                                    <strong>{o.bookTitle}</strong><br/>
                                    <span className={`format-badge ${o.category?.toLowerCase()}`}>
                                        {o.category || 'LIBRO'}
                                    </span>
                                </td>
                                <td>
                                    <span style={{color: '#2e7d32', fontWeight: 'bold'}}>${o.amountUSD}</span><br/>
                                    <small style={{color: '#d32f2f'}}>Bs. {o.amountBS}</small>
                                </td>
                                <td>
                                    <strong>{o.referenceNumber}</strong><br/>
                                    <small>{o.bankName}</small>
                                </td>
                                <td>
                                    {o.receiptKey ? (
                                        <button 
                                            className="btn-view-small"
                                            onClick={() => handleViewReceipt(o.receiptKey)}
                                            style={{ padding: '6px 10px', cursor: 'pointer', backgroundColor: '#D4AF37', color: '#001f3f', border: 'none', borderRadius: '4px', fontWeight: 'bold' }}
                                        >
                                            Ver Foto
                                        </button>
                                    ) : (
                                        <span style={{fontSize: '11px', color: '#999'}}>Sin capture</span>
                                    )}
                                </td>
                                <td>
                                    <span className={`status-badge ${o.status.toLowerCase()}`}>
                                        {o.status}
                                    </span>
                                    {o.pdfKey && <div style={{fontSize: '9px', color: 'green'}}>PDF Vinculado</div>}
                                </td>
                                <td>
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        {(o.status === 'PENDIENTE' || o.status === 'PENDING') && (
                                            <>
                                                <button title="Aprobar" onClick={() => handleApprove(o)} style={{ backgroundColor: '#2e7d32', color: 'white', border: 'none', padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>
                                                    <FiCheck />
                                                </button>
                                                <button title="Rechazar" onClick={() => handleReject(o.id)} style={{ backgroundColor: '#d32f2f', color: 'white', border: 'none', padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>
                                                    <FiX />
                                                </button>
                                            </>
                                        )}
                                        <button title="Eliminar Registro" onClick={() => handleDeleteOrder(o.id)} style={{ backgroundColor: '#000', color: 'white', border: 'none', padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>
                                            <FiTrash2 />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};